import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import TextInputWithLabel from "../../components/TextInputWithLabel";
import {DefaultButton, ModalContainer, ModalContent} from "../../resources/styles";
import {getFromHubNew, getResourceImageUrl, postToHubNew} from "../../actions/networkActions";
import {store} from "../../App";
import {SET_AUTHENTICATION, SET_SESSION_ID} from "../../constants/authentication-action-types";
import FullScreenLoader from '../../components/FullScreenLoader';
import PropTypes from "prop-types";
import {setSelectedCurrency} from "../../actions/currencyActions";
import {getTranslation} from "../../translations/translationUtils";
import {removeHtmlStyles} from "../../utils/stringUtils";

function RescoLogin(props, context) {
    const [submitting, setSubmitting] = useState(false);
    const [cardNr, setCardNr] = useState("");
    const [cabinNr, setCabinNr] = useState("");
    const [pin, setPin] = useState("");
    const [error, setError] = useState("");
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loginResult, setLoginResult] = useState(null);
    const [showPinChangeModal, setShowPinChangeModal] = useState(false);
    const pinRequired = useSelector(state => state.configuration.hubSettings?.pmsConfiguration?.pinLoginEnabled === "true");
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchData = async () => {
            const articles = await getFromHubNew("cms/articles/findByHashtag?hashtag=loginFormExtra");
            if(articles.length === 0) {
                setLoading(false);
                return;
            }
            setArticle(articles[0]);
            setLoading(false);
        }

        fetchData();

    }, []);

    if(loading) {
        return (
            <FullScreenLoader />
        )
    }

    const submit = async (e) => {
        e.preventDefault();
        if(submitting) {
            return;
        }

        if(cardNr === "" || cabinNr === "" || (pinRequired && pin === "")) {
            setError(context.t('error_empty_fields'));
            return;
        }

        const payload = {
            accountNumber: cardNr,
            location: cabinNr,
        }
        if(pinRequired) {
            payload.pinNo = pin;
        }

        setSubmitting(true);
        setError("");

        const submitUrl = pinRequired ? "authentication/loginByAccountIdCabinNoAndPinNo" : "authentication/loginByPms"
        const result = await postToHubNew(submitUrl, payload);

        setSubmitting(false);

        if(result) {
            const pmsInfoObject = JSON.parse(result.pms.information);
            if (pmsInfoObject.settlementCurrencySymbol) {
                dispatch(setSelectedCurrency(pmsInfoObject.settlementCurrencySymbol));
            }

            if(pinRequired) {
                const isPinChangeRequired = pmsInfoObject.isPinChangeRequired;

                if(isPinChangeRequired) {
                    store.dispatch({type: SET_SESSION_ID, sessionId: result.sessionId});

                    setLoginResult(result);
                    setShowPinChangeModal(true);
                    return;
                }
            }

            store.dispatch({type: SET_AUTHENTICATION, authentication: result});
        }
        else {
            setError(context.t('loginFailed'))
        }
    }

    return (
        <div>
            <form onSubmit={(e) => submit(e)}>
                <div className="grid-container">
                    {article &&
                    <div className="grid-x grid-padding-x align-center">
                        <div className="cell small-10 medium-4 large-4 text-left">
                            <div className="text-sm">
                                <p dangerouslySetInnerHTML={{__html: removeHtmlStyles(getTranslation(article.description))}} />
                            </div>
                            <div className="mt-4">
                                <img style={{width: "100%"}} src={getResourceImageUrl(article.id, {width: 420})} alt="onboard card example" />
                            </div>
                        </div>
                    </div>
                    }

                    <div className="grid-x grid-padding-x align-center my-4">
                        <div className="cell small-10 medium-4 large-4 text-left">
                            <TextInputWithLabel
                                label={context.t('account_nr')}
                                value={cardNr}
                                _onChange={value => setCardNr(value)}
                                placeholder="12345678"
                            />
                        </div>
                    </div>

                    <div className="grid-x grid-padding-x align-center my-4">
                        <div className="cell small-10 medium-4 large-4 text-left">
                            <TextInputWithLabel
                                label={context.t('cabin_nr')}
                                value={cabinNr}
                                _onChange={value => setCabinNr(value)}
                                placeholder="1234"
                            />
                        </div>
                    </div>

                    {pinRequired &&
                    <div className="grid-x grid-padding-x align-center my-4">
                        <div className="cell small-10 medium-4 large-4 text-left">
                            <TextInputWithLabel
                                label={context.t('pin_nr')}
                                value={pin}
                                _onChange={value => setPin(value)}
                                placeholder="0101"
                            />
                        </div>
                    </div>
                    }

                    {error &&
                    <div className="grid-x grid-padding-x align-center mt-4">
                        <div className="cell small-10 medium-4 large-4 text-left errors">
                            {error}
                        </div>
                    </div>}

                    <div className="grid-x grid-padding-x align-center mt-4">
                        <div className="cell small-10 medium-4 large-4 text-left">
                            <DefaultButton type="submit" disabled={submitting}>
                                {context.t('login')}
                            </DefaultButton>
                        </div>
                    </div>
                </div>
            </form>

            {showPinChangeModal &&
            <PinChangeModal
                onClose={() => setShowPinChangeModal(false)}
                oldPin={pin}
                loginResult={loginResult}
            />
            }
        </div>
    );
}

RescoLogin.contextTypes = {
    t: PropTypes.func.isRequired
}

export default RescoLogin;

function PinChangeModal(props, context) {
    const [pin, setPin] = useState("");
    const [error, setError] = useState("");

    const validate = () => {
        const re = /^[0-9]{4}$/;
        return re.test(pin);
    }

    const setNewPin = async () => {
        if(!validate()) {
            setError("validation");
            return;
        }

        const payload = {
            "currentPin": props.oldPin,
            "newPin": pin,
        }

        const result = await postToHubNew("pms/changePin", payload)

        if(result === "") {
            store.dispatch({type: SET_AUTHENTICATION, authentication: props.loginResult});
        }
        else {
            setError("general")
        }
    }

    return (
        <ModalContainer>
            <ModalContent>
                <div>
                    <div className="bg-white p-4">
                        <div className="text-lg border-b">
                            {context.t('change_pin')}
                        </div>

                        <div className="mt-4 text-lg">
                            <TextInputWithLabel
                                label={context.t('new_pin')}
                                value={pin}
                                _onChange={value => setPin(value)}
                                placeholder="0101"
                            />
                        </div>

                        <div className="mt-4">
                            {context.t('pin_length_error')}
                        </div>
                        <div>
                            {context.t('pin_change_info')}
                        </div>
                        {error &&
                        <div className="errors mt-4">
                            {error === "validation" ?
                                context.t('new_pin_length')
                                :
                                context.t('error_pin_change')
                            }
                        </div>}
                    </div>
                    <div className="justify-between flex mt-4">
                        {error !== "general" &&
                        <DefaultButton onClick={setNewPin}>
                            {context.t('confirm')}
                        </DefaultButton>
                        }

                        <DefaultButton onClick={props.onClose}>
                            {context.t('back')}
                        </DefaultButton>
                    </div>
                </div>
            </ModalContent>
        </ModalContainer>
    )
}

PinChangeModal.contextTypes = {
    t: PropTypes.func.isRequired
}